import React, {useState, useEffect, forwardRef, useImperativeHandle} from 'react';
import { Button, Modal, Toast } from 'antd-mobile';
import {PhoneFill, LocationOutline, MessageOutline, CheckCircleFill} from 'antd-mobile-icons';
import {t} from "../../../../i18n";
import { openInAppNavigation } from 'utils/appRuntime';
import { openWazeNavigation, getNavigationMapTypeFromStorage, NavigationMapType, checkLongPressEnabled, forceCloseNavigationModal } from '../../../../utils/navigationUtils';
import { useLongPress } from '../../../../hooks/useLongPress';
import { nativeCallbacks, NativeCallbackManager } from 'utils/NativeCallbackManager';
import {getUserInfo} from "../../../../utils/auth";
import {
    isAppVersionGreaterThanOrEqual,
    kAndroidGoogleMapVersion,
    checkWazeSupport
} from "../../../../utils/versionUtils";
import {isAndroid} from "../../../../utils/deviceUtils";
import {useNavigate} from "react-router-dom";
import {DeliveringPath} from "../../../../enums/DeliveryNavigatePathEnum";
import {isSmsTemplateGrayEnabled} from "../../../../utils/gray/SmsTemplateModelGray";

interface DeliveryPhoneAndNavigateProps {
    phone: string;
    address: string;
    addressLine1?: string;
    isNavigationAddressAnalyzeEnabled?: boolean;
    tracking?: string;
    latitude?: number;
    longitude?: number;
    stopNumber?: string;
    fullName?: string;
    subStopNumber?: string;
    driverId?: number;
    driverName?:String;
    orderAddressId?: number;
    dspId?: number;
    onDeliverySuccessFromNative?: () => void;
    onDeliveryFailedFromNative?: () => void;
}

export interface DeliveryPhoneAndNavigateRef {
    closeLongPressModal: () => void;
}

const DeliveryPhoneAndNavigate = forwardRef<DeliveryPhoneAndNavigateRef, DeliveryPhoneAndNavigateProps>(({
    phone,
    address,
    addressLine1,
    isNavigationAddressAnalyzeEnabled,
    tracking,
    onDeliverySuccessFromNative,
    onDeliveryFailedFromNative,
    latitude,
    longitude,
    stopNumber,
    fullName,
    subStopNumber,
    driverId,
    driverName,
    orderAddressId,
    dspId
}, ref) => {
    const [mapSelectionVisible, setMapSelectionVisible] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [selectedMap, setSelectedMap] = useState<NavigationMapType | null>(null);
    const [isWazeSupported, setIsWazeSupported] = useState(false);
    const [isLongPressEnabled, setIsLongPressEnabled] = useState(false);
    const userInfo = getUserInfo();
    const reviewAccount = userInfo?.account?.id === '216';
    const navigate = useNavigate();

    // 长按检测 Hook
    const { handleLongPressStart, handleLongPressEnd, isLongPressTriggered } = useLongPress(() => {
        if (address && address.trim() !== '') {
            // 弹窗打开时，确保 selectedMap 有值
            setSelectedMap(getNavigationMapTypeFromStorage());
            setMapSelectionVisible(true);
        }
    });

// 在组件内部
    const [isEnabled, setIsEnabled] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 监听全局关闭事件（用于集群模式）
        const handleGlobalClose = () => {
            setMapSelectionVisible(false);
            setSelectedMap(null);
        };

        window.addEventListener('close-all-navigation-modals', handleGlobalClose);
        return () => {
            window.removeEventListener('close-all-navigation-modals', handleGlobalClose);
        };
    }, []);

    useEffect(() => {
        async function checkGrayStatus() {
            // dsp与driver共用组件，防御性检查
            if (!dspId || !driverId) {
                setIsEnabled(false);
                setLoading(false);
                return;
            }
            try {
                // 由于上面已经判断了dspId和driverId都存在，这里使用非空断言是安全的
                const result = await isSmsTemplateGrayEnabled(dspId!!, driverId!!);
                setIsEnabled(result);
            } finally {
                setLoading(false);
            }
        }

        checkGrayStatus();
    }, [1, driverId]);



    useEffect(() => {
        //当前账号非提审账号时将且导航应用为inapp将其设置为google地图
        if (localStorage.getItem('navigationApp') === 'inapp' && !reviewAccount) {
            localStorage.setItem('navigationApp', 'google');
        }
        
        // 初始化选中的地图（从 localStorage 读取默认值）
        setSelectedMap(getNavigationMapTypeFromStorage());
    }, [reviewAccount]);

    useEffect(() => {
        // 检测是否为iOS设备
        const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
        setIsIOS(ios);

        // 检查是否支持Waze导航
        checkWazeSupport().then(setIsWazeSupported);

        // 检查是否启用长按功能
        checkLongPressEnabled().then(setIsLongPressEnabled);

        // 注册原生回调
        nativeCallbacks.on(NativeCallbackManager.DELIVERY_SUCCESS, handleDeliverySuccessFromNative);
        nativeCallbacks.on(NativeCallbackManager.DELIVERY_FAILED, handleDeliveryFailedFromNative);

        // 清理函数
        return () => {
            nativeCallbacks.off(NativeCallbackManager.DELIVERY_SUCCESS, handleDeliverySuccessFromNative);
            nativeCallbacks.off(NativeCallbackManager.DELIVERY_FAILED, handleDeliveryFailedFromNative);
        };
    }, []);

    // 暴露关闭长按弹窗的方法给父组件
    useImperativeHandle(ref, () => ({
        closeLongPressModal: () => {
            setMapSelectionVisible(false);
            setSelectedMap(null);
            // iOS 18 需要更强制的方式关闭弹窗
            // 使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
            requestAnimationFrame(() => {
                forceCloseNavigationModal(
                    t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                    t('pages.delivery.orderDetail.mapCancel') || undefined
                );
            });
        }
    }));

    // 处理来自原生的派送成功回调
    const handleDeliverySuccessFromNative = () => {
        onDeliverySuccessFromNative?.();
    };

    // 处理来自原生的派送失败回调
    const handleDeliveryFailedFromNative = () => {
        onDeliveryFailedFromNative?.();
    };

    const handleCall = () => {
        if (!phone || phone.trim() === '') {
            Toast.show({
                content: 'No phone number available',
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = phone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: 'Invalid phone number format',
                position: 'bottom',
            });
            return;
        }

        try {
            // 尝试使用原生桥接
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: 'Phone call failed, please dial manually: ' + cleanPhone,
                position: 'bottom',
                duration: 3000
            });
        }
    };

    const handleNavigate = () => {
        // 如果触发了长按，不执行单击导航
        if (isLongPressTriggered) {
            return;
        }

        if (!address || address.trim() === '') {
            Toast.show({
                content: 'No address available',
                position: 'bottom',
            });
            return;
        }

        // 获取用户设置的默认导航应用
        const defaultNavigationApp = localStorage.getItem('navigationApp') || 'google';

        // 如果用户设置了默认导航应用，直接使用
        if (defaultNavigationApp === 'apple' || defaultNavigationApp === 'google' || defaultNavigationApp === 'inapp' || defaultNavigationApp === 'waze') {
            const mapType = getNavigationMapTypeFromStorage();
            handleSelectMap(mapType);
        } else {
            // 如果没有设置默认应用，显示选择对话框
            setMapSelectionVisible(true);
        }
    };

    const handleSms = () => {
        if (!stopNumber || !fullName) {
            Toast.show({
                content: 'Missing required information for SMS',
                position: 'bottom',
            });
            return;
        }

        navigate(DeliveringPath.DELIVERY_CHAT, {
            state: {
                stopNumber,
                fullName,
                phone,
                tracking,
                subStopNumber,
                driverId,
                orderAddressId,
                driverName
            }
        });
    };

    const openUrl = (url: string) => {
        // 直接跳转；原生 WebView 要把自定义 scheme 拦截出去
        window.location.href = url;
    };

    const handleSelectMap = async (mapType: NavigationMapType) => {
        setSelectedMap(mapType);
        // iOS 18 需要更强制的方式关闭弹窗
        setMapSelectionVisible(false);
        // 使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
        requestAnimationFrame(() => {
            forceCloseNavigationModal(
                t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                t('pages.delivery.orderDetail.mapCancel') || undefined
            );
        });

        if (!address.trim()) {
            Toast.show({ content: 'No address', position: 'bottom' });
            return;
        }

        const androidGoogleOptimize = await isAppVersionGreaterThanOrEqual(kAndroidGoogleMapVersion);
        if(latitude && longitude){
            newVersionNavigation(mapType,androidGoogleOptimize);
        }else {
            oldVersionNavigation(mapType,androidGoogleOptimize);
        }

    };

    //非灰度一定走老版本 非灰度司机新版本安卓app包
    const newVersionNavigation = (mapType: string, androidGoogleOptimize: boolean) => {
        if (!isNavigationAddressAnalyzeEnabled){
            //老版本逻辑根据地址导航
            oldVersionNavigation(mapType, androidGoogleOptimize);
            return;
        }

        //灰度
        const encoded = encodeURIComponent(addressLine1 || address);
        // —— 首先尝试 JS-Bridge（最稳） ——
        if (mapType === 'Google') {
            if (window.AndroidApp?.openNavigation) {
                if (isAndroid() && androidGoogleOptimize){
                    window.AndroidApp.openNavigation(`google.navigation:q=${encoded}`);
                }else {
                    window.AndroidApp.openNavigation(address);
                }
            }else if (isIOS){
                const iOSScheme = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
                openUrl(iOSScheme);
            }
            return;
        }

        if (mapType === 'Apple') {
            if (!isIOS) {
                Toast.show({ content: 'Apple Maps only on iOS', position: 'bottom' });
                return;
            }
            openUrl(`maps://?q=${encoded}`);
        }

        if (mapType === 'Inapp') {
            // latitude：34.17501, longitude:-118.39651
            if (latitude && longitude) {
                openInAppNavigation(tracking || '', phone, address, latitude.toString(), longitude.toString());
            } else {
                Toast.show({content: 'latitude ｜ longitude is empty',position: 'bottom',});
            }
        }

        if (mapType === 'Waze') {
            openWazeNavigation(addressLine1 || address, latitude, longitude);
        }
    };

    const oldVersionNavigation = (mapType: string, androidGoogleOptimize: boolean) => {
        const encoded = encodeURIComponent(address);

        // —— 首先尝试 JS-Bridge（最稳） ——
        if (mapType === 'Google') {
            if (window.AndroidApp?.openNavigation) {
                if (androidGoogleOptimize){
                    window.AndroidApp.openNavigation(`google.navigation:q=${encoded}`);
                }else {
                    window.AndroidApp.openNavigation(address);
                }
                return;
            }
            // —— 无桥接时：尝试调起 Google Maps ——

            if (isIOS) {
                const iOSScheme = `comgooglemaps://?daddr=${encoded}&directionsmode=driving`;
                openUrl(iOSScheme);
            } else {
                const androidScheme = `google.navigation:q=${encoded}`;
                openUrl(androidScheme);
                // Android WebView 若未被原生拦截，也 fallback
                setTimeout(() => openUrl(`https://www.google.com/maps/dir/?api=1&destination=${encoded}`), 2000);
            }
        }

        if (mapType === 'Apple') {
            if (!isIOS) {
                Toast.show({ content: 'Apple Maps only on iOS', position: 'bottom' });
                return;
            }
            openUrl(`maps://?q=${encoded}`); // iOS 官方文档
        }

        if (mapType === 'Inapp') {
            // latitude：34.17501, longitude:-118.39651
            if (latitude && longitude) {
                openInAppNavigation(tracking || '', phone, address, latitude.toString(), longitude.toString());
            } else {
                Toast.show({content: 'latitude ｜ longitude is empty',position: 'bottom',});
            }
        }

        if (mapType === 'Waze') {
            openWazeNavigation(address, latitude, longitude);
        }
    };

    return (
        <>
            <div className='flex gap-2 justify-between'>
                <Button
                    color='default'
                    shape='rounded'
                    onClick={handleCall}
                    block
                    style={{ '--background-color': '#f0f0f0' }}
                >
                    <div className='flex flex-row justify-center items-center gap-2'>
                        <PhoneFill />
                        <div>{t('pages.delivery.orderDetail.newCall')}</div>
                    </div>
                </Button>
                {!loading && isEnabled && (
                    <Button
                        color='default'
                        shape='rounded'
                        onClick={handleSms}
                        block
                        style={{ '--background-color': '#f0f0f0' }}
                    >
                        <div className='flex flex-row justify-center items-center gap-2'>
                            <MessageOutline />
                            <div>{t('pages.delivery.orderDetail.sms')}</div>
                        </div>
                    </Button>
                )}
                <Button
                    color='default'
                    shape='rounded'
                    onClick={handleNavigate}
                    {...(isLongPressEnabled && {
                        onMouseDown: handleLongPressStart,
                        onMouseUp: handleLongPressEnd,
                        onTouchStart: handleLongPressStart,
                        onTouchEnd: handleLongPressEnd
                    })}
                    block
                    style={{ '--background-color': '#f0f0f0' }}
                >
                    <div className='flex flex-row justify-center items-center gap-2'>
                        <LocationOutline />
                        <div>{t('pages.delivery.orderDetail.navigate')}</div>
                    </div>
                </Button>
            </div>

            {/* Map selection modal */}
            <Modal
                visible={mapSelectionVisible}
                title= {t('pages.delivery.orderDetail.selectMapForThisTimeTitle')}
                closeOnMaskClick
                onClose={() => {
                    setMapSelectionVisible(false);
                    // iOS 18 需要更强制的方式关闭弹窗
                    requestAnimationFrame(() => {
                        forceCloseNavigationModal(
                            t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                            t('pages.delivery.orderDetail.mapCancel') || undefined
                        );
                    });
                }}
                content={(
                    <div className="py-4">
                        {(() => {
                            // 确保在弹窗显示时有选中的值
                            let currentSelected: NavigationMapType = selectedMap || getNavigationMapTypeFromStorage();
                            
                            return (
                                <>
                                    <div
                                        className="py-3 px-4 text-base text-center border-b border-gray-100 flex items-center justify-between"
                                        onClick={() => handleSelectMap('Google')}
                                    >
                                        <span>{t('pages.delivery.orderDetail.googleMaps')}</span>
                                        {currentSelected === 'Google' ? (
                                            <CheckCircleFill color="#00b578" style={{fontSize: '20px'}}/>
                                        ) : (
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                border: '2px solid #c9c9c9',
                                                flexShrink: 0
                                            }}/>
                                        )}
                                    </div>
                                    {isIOS && (
                                        <div
                                            className="py-3 px-4 text-base text-center border-b border-gray-100 flex items-center justify-between"
                                            onClick={() => handleSelectMap('Apple')}
                                        >
                                            <span>{t('pages.delivery.orderDetail.appleMaps')}</span>
                                            {currentSelected === 'Apple' ? (
                                                <CheckCircleFill color="#00b578" style={{fontSize: '20px'}}/>
                                            ) : (
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #c9c9c9',
                                                    flexShrink: 0
                                                }}/>
                                            )}
                                        </div>
                                    )}
                                    {isWazeSupported && (
                                        <div
                                            className="py-3 px-4 text-base text-center flex items-center justify-between"
                                            onClick={() => handleSelectMap('Waze')}
                                        >
                                            <span>{t('pages.delivery.orderDetail.wazeMaps')}</span>
                                            {currentSelected === 'Waze' ? (
                                                <CheckCircleFill color="#00b578" style={{fontSize: '20px'}}/>
                                            ) : (
                                                <div style={{
                                                    width: '20px',
                                                    height: '20px',
                                                    borderRadius: '50%',
                                                    border: '2px solid #c9c9c9',
                                                    flexShrink: 0
                                                }}/>
                                            )}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}
                actions={[
                    {
                        key: 'cancel',
                        text: t('pages.delivery.orderDetail.mapCancel'),
                        onClick: () => {
                            setMapSelectionVisible(false);
                            // iOS 18 需要更强制的方式关闭弹窗
                            requestAnimationFrame(() => {
                                forceCloseNavigationModal(
                                    t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                                    t('pages.delivery.orderDetail.mapCancel') || undefined
                                );
                            });
                        },
                    }
                ]}
            />
        </>
    );
});

DeliveryPhoneAndNavigate.displayName = 'DeliveryPhoneAndNavigate';

export default DeliveryPhoneAndNavigate;